import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';

import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { PaymentGateway } from '../../../../../shared/enums/payment-gateway.enum';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';

interface PaymentActionsDropdownProps {
  payment: PaymentModel;
  isPermited: (roles: UserRole[]) => boolean;
  reservationSource?: string;
  isCheckingPaymobStatus?: boolean;
  isSwitchingGateway?: boolean;
  onSend: () => void;
  onPartial: () => void;
  onSplitPayment: () => void;
  onGenerateLink: () => void;
  onAddReceipt: () => void;
  onViewHistory: () => void;
  onResendConfirmation: () => void;
  onCheckPaymobStatus: () => void;
  onSwitchGateway: (targetGateway: PaymentGateway) => void;
}

export function PaymentActionsDropdown({
  payment,
  isPermited,
  reservationSource,
  isCheckingPaymobStatus = false,
  isSwitchingGateway = false,
  onSend,
  onPartial,
  onSplitPayment,
  onGenerateLink,
  onAddReceipt,
  onViewHistory,
  onResendConfirmation,
  onCheckPaymobStatus,
  onSwitchGateway
}: PaymentActionsDropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; openUp: boolean }>({
    top: 0,
    left: 0,
    openUp: false
  });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        btnRef.current &&
        !btnRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isNotCollectedOrRefunded =
    payment.status !== PaymentStatus.REFUNDED && payment.status !== PaymentStatus.COLLECTED;

  const canTakeAction =
    isNotCollectedOrRefunded && payment.direction !== 'out' && payment.paymentAmount > 0;

  const hasPaymentLink = !!payment.birdnestPaymentLink;

  const isFinanceRole = isPermited([
    UserRole.FINANCE_MANAGER,
    UserRole.FINANCE_SPECIALIST,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  const isReservationTeamRole = isPermited([
    UserRole.RESERVATIONS,
    UserRole.RESERVATION_MANAGER,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  // Show send only if payment link exists
  const showSend = canTakeAction && hasPaymentLink;

  // Show partial for finance roles when not collected/refunded
  const showPartial = canTakeAction && hasPaymentLink && isFinanceRole;

  // Show split when not collected/refunded, direction !== 'out', amount > 0
  const showSplit =
    reservationSource !== 'channel-manager' &&
    isNotCollectedOrRefunded &&
    payment.direction !== 'out' &&
    payment.paymentAmount > 0;

  // Show generate link when no link exists
  const showGenerateLink = canTakeAction && !hasPaymentLink;

  // Show add receipt when collected/partially collected/refunded
  const showAddReceipt =
    payment.status === PaymentStatus.COLLECTED ||
    payment.status === PaymentStatus.PARTIALLY_COLLECTED ||
    payment.status === PaymentStatus.REFUNDED;

  // A confirmation email only exists for money the guest actually paid, and resending it
  // needs the same permission admin-connect enforces on the endpoint.
  const showResendConfirmation =
    hasAccess([
      PERMISSIONS_LIST.reservationsFullAccess,
      PERMISSIONS_LIST.reservationsModifyAccess
    ]) &&
    payment.direction !== 'out' &&
    (payment.status === PaymentStatus.COLLECTED ||
      payment.status === PaymentStatus.PARTIALLY_COLLECTED);

  const showCheckPaymobStatus =
    isReservationTeamRole &&
    payment.id &&
    [PaymentStatus.OVERDUE, PaymentStatus.PENDING, PaymentStatus.CANCELED].includes(payment.status);

  payment.paymentGateway === PaymentGateway.PAYMOB &&
    [PaymentStatus.OVERDUE, PaymentStatus.PENDING, PaymentStatus.CANCELED].includes(payment.status);

  const targetGateway =
    payment.paymentGateway === PaymentGateway.PAYMOB
      ? PaymentGateway.GEIDEA
      : PaymentGateway.PAYMOB;

  const showSwitchGateway =
    (isReservationTeamRole || isFinanceRole) &&
    !!payment.id &&
    !!payment.paymentGateway &&
    payment.direction !== 'out' &&
    payment.paymentAmount > 0 &&
    [PaymentStatus.PENDING, PaymentStatus.OVERDUE, PaymentStatus.CANCELED].includes(payment.status);

  // Show copy link when payment link exists
  const showCopyLink = hasPaymentLink;

  // Show view history for all payments
  const showViewHistory = true;

  const hasAnyAction =
    showSend ||
    showPartial ||
    showSplit ||
    showGenerateLink ||
    showAddReceipt ||
    showResendConfirmation ||
    showCopyLink ||
    showViewHistory ||
    showCheckPaymobStatus ||
    showSwitchGateway;

  if (!hasAnyAction) {
    return <span style={{ width: '24px' }} />;
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const actionCount = [
              showCopyLink,
              showSend,
              showPartial,
              showSplit,
              showGenerateLink,
              showAddReceipt,
              showResendConfirmation,
              showCheckPaymobStatus,
              showSwitchGateway
            ].filter(Boolean).length;
            const estimatedHeight = actionCount * 40 + 8;
            const spaceBelow = window.innerHeight - rect.bottom - 4;
            const openUp = spaceBelow < estimatedHeight && rect.top > estimatedHeight;
            setDropdownPos({
              top: openUp ? rect.top - 4 : rect.bottom + 4,
              left: rect.right,
              openUp
            });
          }
          setOpen(!open);
        }}
        className="flex cursor-pointer items-center justify-center rounded-md border-0 bg-transparent px-2 py-1 text-neutral-500 transition-colors hover:bg-gray-100"
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={ref}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              transform: dropdownPos.openUp ? 'translate(-100%, -100%)' : 'translateX(-100%)',
              minWidth: '170px',
              zIndex: 9999
            }}
          >
            {showCopyLink && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  navigator.clipboard.writeText(payment.birdnestPaymentLink || '').then(() => {
                    birdToast.success('Payment link copied!');
                  });
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left font-gilroy-medium font-semibold text-xl text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy Link
              </button>
            )}
            {showSend && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSend();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                </svg>
                Send
              </button>
            )}
            {showPartial && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onPartial();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                Partial
              </button>
            )}
            {showSplit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSplitPayment();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 3h5v5" />
                  <line x1="4" y1="20" x2="21" y2="3" />
                  <path d="M21 16v5h-5" />
                  <line x1="15" y1="15" x2="21" y2="21" />
                  <line x1="4" y1="4" x2="9" y2="9" />
                </svg>
                Split Payment
              </button>
            )}
            {showGenerateLink && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onGenerateLink();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Generate Link
              </button>
            )}
            {showAddReceipt && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onAddReceipt();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                Add Receipt
              </button>
            )}
            {showResendConfirmation && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onResendConfirmation();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Resend Confirmation
              </button>
            )}
            {showViewHistory && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onViewHistory();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                View History
              </button>
            )}
            {showCheckPaymobStatus && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (!isCheckingPaymobStatus) {
                    onCheckPaymobStatus();
                  }
                }}
                disabled={isCheckingPaymobStatus}
                className={`flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold transition-colors hover:bg-gray-50 ${
                  isCheckingPaymobStatus
                    ? 'cursor-not-allowed text-gray-400 opacity-70'
                    : 'text-neutral-500'
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <polyline points="21 3 21 9 15 9" />
                </svg>
                {isCheckingPaymobStatus ? 'Checking...' : 'Check Paymob Status'}
              </button>
            )}
            {showSwitchGateway && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (!isSwitchingGateway) {
                    onSwitchGateway(targetGateway);
                  }
                }}
                disabled={isSwitchingGateway}
                className={`flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold transition-colors hover:bg-gray-50 ${
                  isSwitchingGateway
                    ? 'cursor-not-allowed text-gray-400 opacity-70'
                    : 'text-neutral-500'
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="17 1 21 5 17 9" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <polyline points="7 23 3 19 7 15" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
                {isSwitchingGateway
                  ? 'Switching...'
                  : `Switch to ${targetGateway === PaymentGateway.GEIDEA ? 'Geidea' : 'Paymob'}`}
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
